import { useState } from "react";
import { Check, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/Button";
import { SegmentedPillToggle } from "@/components/SegmentedPillToggle";

const OVERSIGHT =
  "AI Standards oversight included — a named human expert reviews and signs off on the work. This never varies by tier. What changes is capacity and speed.";

const TERMS_HEADING =
  "Sign month-to-month or annual — flexible terms. You choose the options.";

const TERMS = [
  "Transparent weekly reporting",
  "No hidden fees",
  "30 / 60 / 90-day plan",
  "Launch in 14 days",
];

// Per-team first two cards. Third card (Full Department) is static below.
const TEAM_TIERS = {
  marketing: [
    {
      key: "priority",
      eyebrow: "Priority",
      scope: "One priority",
      price: 500,
      intro: "Your audit names your single biggest marketing constraint. The team owns it.",
      features: [
        "One marketing priority owned end to end — named in writing in your proposal",
        "One channel managed",
        "4 videos + 4 statics/mo, scoped to that priority",
        "Monthly report with one clear recommendation",
        "Weekly batch review turnaround",
      ],
    },
    {
      key: "full-team",
      eyebrow: "Full Team",
      scope: "The whole function",
      price: 1500,
      badge: "Most hired",
      intro: "Your marketing, run end to end by one team.",
      features: [
        "Up to 3 channels managed",
        "10 videos + 10 statics/mo",
        "Content calendar managed",
        "Review generation and one active outreach sequence",
        "AI Marketer + AI Reputation Agent configured",
        "Bi-weekly 30-minute strategy call",
        "48-hour turnaround",
      ],
    },
  ],
  workflow: [
    {
      key: "priority",
      eyebrow: "Priority",
      scope: "One priority",
      price: 500,
      intro: "Your audit names your single biggest operations constraint. The team owns it.",
      features: [
        "One operations priority owned end to end — named in writing in your proposal",
        "2 AI agents configured from our roster (receptionist, scheduler, or sales agent) — whichever your priority needs",
        "100 AI voice minutes/mo",
        "One automation build/mo connecting your agents to your existing tools",
        "Monthly report with one clear recommendation",
        "Weekly batch review turnaround",
      ],
    },
    {
      key: "full-team",
      eyebrow: "Full Team",
      scope: "The whole function",
      price: 1500,
      badge: "Most hired",
      intro: "Your operations, run end to end by one team.",
      features: [
        "3–4 automation builds/mo including cross-tool integrations",
        "Quarterly bottleneck audit",
        "AI Receptionist, AI Scheduler + AI Sales Agent configured",
        "250 AI voice minutes/mo",
        "Bi-weekly 30-minute strategy call",
        "48-hour turnaround",
      ],
    },
  ],
};

const FULL_DEPARTMENT = {
  key: "full-department",
  eyebrow: "Full Department",
  scope: "The full department",
  price: 5000,
  intro: "Full funnel and full operations ownership, one point of accountability.",
  features: [
    "Everything in both Full Team tiers, run concurrently",
    "Full funnel ownership: 20+ mixed assets/mo including generative film and motion work, monthly campaign concept, paid ads managed (ad spend billed to you directly, never marked up)",
    "Full operations ownership: priority builds, unlimited maintenance subject to fair use, quarterly systems review with leadership",
    "All 5 AI agents deployed",
    "AI voice minutes included, subject to fair use",
    "Dedicated operator, 24-hour turnaround",
  ],
};

const FINE_PRINT = [
  "Agent configuration is prescribed by your audit and approved by you — same price, different roster, built for your business.",
  "AI voice minutes cover inbound, outbound, and web-widget calls combined. Minute packs: 250 for $49 · 600 for $99 · 1,500 for $199. Packs expire with your billing cycle. We alert you before you reach your included minutes — no surprise invoices.",
  "Unlimited usage on the Full Department tier is subject to platform fair-use policies. Standard telephony carrier charges are included in your plan.",
  "Capacity resets monthly, like payroll. Unused deliverables and minutes don't roll over.",
  "Builds requiring more than ~4 expert hours are scoped as one-time projects at any tier.",
  "Upgrade any time, effective immediately. Downgrade at your next billing cycle. Month to month, cancel anytime.",
];

const TierCard = ({ tier, delay }) => (
  <Reveal delay={delay} className={tier.badge ? "order-first lg:order-none" : ""}>
    <div
      className={`relative flex h-full flex-col rounded-2xl border bg-[var(--bg-base)] p-8 transition-all duration-300 hover:-translate-y-1 ${
        tier.badge
          ? "border-2 border-[var(--ember)]/50 shadow-[0_24px_50px_-28px_rgba(0,245,160,0.35)] lg:-mt-4"
          : "border-black/5"
      }`}
      data-testid={`pricing-card-${tier.key}`}
    >
      {tier.badge && (
        <span className="absolute -top-3 left-8 rounded-full bg-[var(--mint)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--charcoal)]">
          {tier.badge}
        </span>
      )}
      <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[var(--ember)]">{tier.eyebrow}</p>
      <p className="mt-1 text-[15px] font-semibold text-[var(--ink)]">{tier.scope}</p>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="text-5xl font-semibold tracking-[-0.03em] text-[var(--ink)]">${tier.price.toLocaleString()}</span>
        <span className="text-[15px] font-medium text-[var(--muted)]">/mo</span>
      </div>
      <p className="mt-1 text-[13px] text-[var(--muted)]">Billed monthly. Cancel anytime.</p>

      <p className="mt-4 text-[14px] leading-relaxed text-[var(--ink)]">{tier.intro}</p>

      <ul className="mt-6 flex-1 space-y-3">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-[14px] leading-snug text-[var(--ink)]">
            <Check size={15} className="mt-0.5 shrink-0 text-[var(--ember)]" />
            {f}
          </li>
        ))}
      </ul>

      <div className="mt-8">
        <Button to="/audit" variant={tier.badge ? "primary" : "secondary"} className="w-full">
          Install your department
        </Button>
      </div>
    </div>
  </Reveal>
);

export const PricingLadder = () => {
  const [team, setTeam] = useState("marketing");
  const teamCards = TEAM_TIERS[team];

  return (
    <div>
      <Reveal>
        <div
          className="relative overflow-hidden rounded-3xl bg-[var(--charcoal)] p-8 sm:p-10"
          data-testid="pricing-terms"
        >
          <div
            className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[var(--mint)]/10 blur-[100px]"
            aria-hidden="true"
          />
          <p className="relative max-w-2xl text-xl font-semibold leading-snug tracking-[-0.01em] text-white sm:text-2xl">
            {TERMS_HEADING}
          </p>
          <div className="relative mt-8 grid gap-x-10 gap-y-5 sm:grid-cols-2">
            {TERMS.map((t) => (
              <div key={t} className="flex items-center gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[var(--mint)]/40 text-[var(--mint)]">
                  <Check size={15} />
                </span>
                <span className="text-[15px] font-medium text-white/90">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* Team toggle — controls the first two card slots */}
      <Reveal>
        <div className="mt-12 flex justify-center" data-testid="pricing-team-toggle-wrap">
          <SegmentedPillToggle
            ariaLabel="Choose your team"
            value={team}
            onChange={setTeam}
            idPrefix="team"
            testId="pricing-team-toggle"
            options={[
              { value: "marketing", label: "Marketing & creative", testId: "pricing-team-marketing" },
              { value: "workflow", label: "Workflow & automation", testId: "pricing-team-workflow" },
            ]}
          />
        </div>
      </Reveal>

      <div className="mt-10 grid gap-6 lg:grid-cols-3">
        {teamCards.map((tier, i) => (
          <TierCard key={`${team}-${tier.key}`} tier={tier} delay={i * 90} />
        ))}
        <TierCard key="full-department" tier={FULL_DEPARTMENT} delay={2 * 90} />
      </div>

      <Reveal>
        <div
          className="mt-6 flex items-start gap-3 rounded-2xl bg-[var(--ember)]/[0.06] p-5 sm:items-center sm:p-6"
          data-testid="pricing-oversight"
        >
          <ShieldCheck size={20} className="mt-0.5 shrink-0 text-[var(--ember)] sm:mt-0" />
          <p className="text-[14px] leading-relaxed text-[var(--ink)] sm:text-[15px]">{OVERSIGHT}</p>
        </div>
      </Reveal>

      <Reveal>
        <p className="mt-8 text-[15px] text-[var(--muted)]" data-testid="pricing-custom-line">
          Multi-location businesses and heavy custom builds get a custom scope —{" "}
          <a href="#final" className="font-semibold text-[var(--ember)] hover:underline">book a call</a>.
        </p>
      </Reveal>

      <div className="mt-12 space-y-2 border-t border-black/10 pt-6" data-testid="pricing-fineprint">
        {FINE_PRINT.map((f) => (
          <p key={f} className="text-[12px] leading-relaxed text-[var(--muted)]">{f}</p>
        ))}
      </div>
    </div>
  );
};
